import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { format } from "date-fns";
import { User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Ad } from "@/models/ad";

interface DashboardActiveAdsProps {
  ads: Ad[];
  isLoading?: boolean;
  title?: string;
}

// Función auxiliar para extraer nombre del email
const extractNameFromEmail = (email: string): string => {
  const nameMatch = email.match(/^([^@]+)@/);
  if (nameMatch && nameMatch[1]) {
    return nameMatch[1].charAt(0).toUpperCase() + nameMatch[1].slice(1);
  }
  return "Desconocido";
};

const DashboardActiveAds: React.FC<DashboardActiveAdsProps> = ({ 
  ads, 
  isLoading = false,
  title = "Anuncios Activos" 
}) => {
  if (isLoading) {
    return (
      <div>
        <h2 className="text-2xl font-bold mb-4">{title}</h2>
        <Card className="p-6">
          <div className="flex flex-col items-center justify-center py-4">
            <p>Cargando anuncios...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (ads.length === 0) {
    return (
      <div>
        <h2 className="text-2xl font-bold mb-4">{title}</h2>
        <Card className="p-6">
          <div className="flex flex-col items-center justify-center py-4">
            <p>No hay anuncios activos</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">{title}</h2>
      <Card>
        <CardHeader>
          <CardTitle>Listado de anuncios</CardTitle>
          <CardDescription>
            Lista completa de todos los anuncios activos en el sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead>Pantalla</TableHead>
                <TableHead>Vendedor</TableHead>
                <TableHead>Correo del Vendedor</TableHead>
                <TableHead>Fecha Inicio</TableHead>
                <TableHead>Fecha Fin</TableHead>
                <TableHead className="text-right">Monto</TableHead>
                <TableHead>Tipo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ads.map((ad) => (
                <TableRow key={ad.id}>
                  <TableCell>{ad.clientName}</TableCell>
                  <TableCell>{ad.screens.map(screen => screen.screenName).join(", ")}</TableCell>
                  <TableCell className="flex items-center">
                    <User size={14} className="mr-1 text-muted-foreground" />
                    <span>
                      {ad.sellerName === "Desconocido" && ad.sellerEmail
                        ? extractNameFromEmail(ad.sellerEmail)
                        : ad.sellerName || "Desconocido"}
                    </span>
                  </TableCell>
                  <TableCell>
                    {ad.sellerEmail && ad.sellerEmail !== "" ? ad.sellerEmail : "Sin correo registrado"}
                  </TableCell>
                  <TableCell>{format(new Date(ad.startDate), 'dd/MM/yyyy')}</TableCell>
                  <TableCell>{format(new Date(ad.endDate), 'dd/MM/yyyy')}</TableCell>
                  <TableCell className="text-right">
                    {new Intl.NumberFormat('es-AR', {
                      style: 'currency',
                      currency: 'ARS'
                    }).format(ad.amount)}
                  </TableCell>
                  <TableCell>
                    {ad.adType === "courtesy" ? (
                      <Badge className="bg-[#ff8a33] hover:bg-[#f07c28] text-white">
                        Cortesía
                      </Badge>
                    ) : (
                      <span>Venta</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardActiveAds;
